import { create } from "zustand";

import type { GebruikerAntwoord } from "@/types/api";

/**
 * Auth store — client state rondom de ingelogde gebruiker.
 *
 * Multi-tab (27-07): access- en refresh-token leven in localStorage
 * zodat álle tabbladen van dezelfde browser één sessie delen. De oude
 * opzet (sessionStorage per tabblad) gaf elk tabblad een eigen kopie
 * van hetzelfde refresh-token; zodra één tabblad roteerde of via de
 * IdleGuard uitlogde, was de jti van alle andere kopieën ingetrokken
 * en logden alle tabbladen één voor één uit.
 *
 * XSS-afweging: een XSS-lek kan sessionStorage net zo goed uitlezen
 * als localStorage — het verschil zat alleen in levensduur. De echte
 * verdediging blijft server-side: rotatie met intrekking, harde logout
 * en 2FA. Zie docs/BESLISSINGEN.md (sessie-multitab 27-07).
 *
 * Het pending-2FA-token blijft wél sessionStorage: dat hoort bij de
 * login-flow van één tabblad en mag niet lekken naar andere tabbladen.
 *
 * Cross-tab-sync: de "storage"-event (vuurt alleen in ándere
 * tabbladen) houdt de zustand-store overal bij: inloggen in één
 * tabblad maakt andere tabbladen direct werkend, uitloggen (of een
 * echt ingetrokken token) logt overal uit.
 */

const ACCESS_KEY = "ks.accessToken";
const REFRESH_KEY = "ks.refreshToken";
const PENDING_2FA_KEY = "ks.pending2fa";

function leesToken(key: string): string | null {
  try {
    const uitLocal = window.localStorage.getItem(key);
    if (uitLocal !== null) return uitLocal;
    // Migratie: tokens leefden vóór 27-07 in window.sessionStorage. Neem een
    // bestaande sessie eenmalig mee zodat de deploy niemand uitlogt.
    const uitSession = window.sessionStorage.getItem(key);
    if (uitSession !== null) {
      window.localStorage.setItem(key, uitSession);
      window.sessionStorage.removeItem(key);
      return uitSession;
    }
    return null;
  } catch {
    return null;
  }
}

function schrijfToken(key: string, value: string | null) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* quota of private mode */
  }
}

function leesPending2FA(): string | null {
  try {
    return window.sessionStorage.getItem(PENDING_2FA_KEY);
  } catch {
    return null;
  }
}

function schrijfPending2FA(value: string | null) {
  try {
    if (value === null) window.sessionStorage.removeItem(PENDING_2FA_KEY);
    else window.sessionStorage.setItem(PENDING_2FA_KEY, value);
  } catch {
    /* quota of private mode */
  }
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  tweefaPendingToken: string | null;
  gebruiker: GebruikerAntwoord | null;
  setTokens: (access: string, refresh: string) => void;
  setPending2FA: (token: string | null) => void;
  setGebruiker: (gebruiker: GebruikerAntwoord | null) => void;
  logoutLokaal: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: leesToken(ACCESS_KEY),
  refreshToken: leesToken(REFRESH_KEY),
  tweefaPendingToken: leesPending2FA(),
  gebruiker: null,

  setTokens: (access, refresh) => {
    schrijfToken(ACCESS_KEY, access);
    schrijfToken(REFRESH_KEY, refresh);
    schrijfPending2FA(null);
    set({ accessToken: access, refreshToken: refresh, tweefaPendingToken: null });
  },

  setPending2FA: (token) => {
    schrijfPending2FA(token);
    set({ tweefaPendingToken: token });
  },

  setGebruiker: (gebruiker) => set({ gebruiker }),

  logoutLokaal: () => {
    schrijfToken(ACCESS_KEY, null);
    schrijfToken(REFRESH_KEY, null);
    schrijfPending2FA(null);
    set({
      accessToken: null,
      refreshToken: null,
      tweefaPendingToken: null,
      gebruiker: null,
    });
  },
}));

/** Sync de store met wat een ánder tabblad in localStorage schreef. */
function syncVanuitOpslag() {
  let access: string | null;
  let refresh: string | null;
  try {
    access = window.localStorage.getItem(ACCESS_KEY);
    refresh = window.localStorage.getItem(REFRESH_KEY);
  } catch {
    return;
  }
  const huidig = useAuthStore.getState();
  if (access === huidig.accessToken && refresh === huidig.refreshToken) return;
  if (access === null && refresh === null) {
    // Ander tabblad logde uit (of het token bleek ingetrokken): ook
    // hier de sessie sluiten, inclusief gebruikersprofiel.
    useAuthStore.setState({ accessToken: null, refreshToken: null, gebruiker: null });
  } else {
    // Login of token-rotatie elders: tokens overnemen. Het profiel
    // blijft staan als het er al was (zelfde browser = zelfde account).
    useAuthStore.setState({ accessToken: access, refreshToken: refresh });
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    // key === null betekent storage.clear(); verder alleen onze keys.
    if (event.key !== null && event.key !== ACCESS_KEY && event.key !== REFRESH_KEY) {
      return;
    }
    syncVanuitOpslag();
  });
}

/** Read toegangstoken zonder de hook — voor axios interceptor. */
export function huidigAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}

export function huidigRefreshToken(): string | null {
  return useAuthStore.getState().refreshToken;
}
